import React, { useState, useMemo, useRef } from 'react';
import QRCode from 'react-qr-code';
import { gerarPixPayload } from '../utils/pixPayload';
import { api } from '../api';
import Modal from './ui/Modal';
import Icon from './ui/Icon';
import { fmtData } from '../utils/format';
import { getFazendas, getFazendaAtiva, trocarFazenda } from '../sessao';

// Dados do recebedor Sicredi
const PIX_CONFIG = {
  chave: '+5554999487383',
  nome: 'ELIARDO MARINI',
  cidade: 'BRASILIA',
};

/**
 * Data em dd/MM/yyyy só quando ela existe e é válida.
 * Antes o componente fazia `new Date(null + 'T00:00:00')` e mostrava
 * "Invalid Date" para o produtor quando o backend não mandava a data.
 */
const dataOuNulo = (iso) => {
  if (!iso) return null;
  const d = new Date(String(iso).slice(0, 10) + 'T00:00:00');
  if (isNaN(d.getTime())) return null;
  const texto = fmtData(iso);
  return texto && texto !== '—' ? texto : null;
};

export default function ModalAssinatura({ assinatura, onClose }) {
  const { status, venceEm, diasRestantes, venceuEm, plano, valorMensal, valorDevido, fazendaId, modoPagamento } = assinatura;
  const bloqueado = !onClose || status === 'VENCIDO';

  const [mostrarPix, setMostrarPix] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [copiado, setCopiado] = useState(false);
  const [erroCopia, setErroCopia] = useState('');
  const [erro, setErro] = useState('');
  const [trocandoFazenda, setTrocandoFazenda] = useState(false);
  const codigoRef = useRef(null);

  // Multi-fazenda: quando esta fazenda está vencida, o usuário pode escapar do
  // paywall trocando para outra fazenda utilizável (ativa e em dia). O bloqueio
  // é por fazenda ativa, não pela conta inteira.
  const ativa = getFazendaAtiva();
  const outrasFazendas = getFazendas().filter((f) =>
    f.vinculoStatus === 'ATIVO'
    && !f.assinaturaVencida
    && (!ativa || String(f.fazendaId) !== String(ativa.fazendaId)));

  const irParaFazenda = async (f) => {
    setTrocandoFazenda(true);
    try {
      await trocarFazenda(f.fazendaId);
      window.location.assign('/');
    } catch (e) {
      setErro(e.message || 'Não foi possível trocar de fazenda.');
      setTrocandoFazenda(false);
    }
  };

  const fmtValor = (v) => v != null ? v.toFixed(2).replace('.', ',') : '0,00';

  const dataVenceEm = dataOuNulo(venceEm);
  const dataVenceuEm = dataOuNulo(venceuEm);
  const dataLiberarAte = dataOuNulo(assinatura.liberarAte);

  const podePagar = status === 'VENCIDO' || (diasRestantes != null && diasRestantes <= 7);
  const valorPagar = status === 'VENCIDO' ? (valorDevido || valorMensal) : valorMensal;

  // Gera payload Pix com o valor já com centavos da fazenda
  const pixPayload = useMemo(() => gerarPixPayload({
    ...PIX_CONFIG,
    valor: valorPagar,
    txid: `AGR${String(fazendaId || 0).padStart(3, '0')}`,
  }), [valorPagar, fazendaId]);

  const handlePagar = async () => {
    setCarregando(true);
    setErro('');

    if (modoPagamento === 'stripe') {
      try {
        const successUrl = window.location.origin + '/?pagamento=sucesso';
        const cancelUrl = window.location.origin + '/?pagamento=cancelado';
        const { url } = await api.criarCheckout(successUrl, cancelUrl);
        window.location.href = url;
      } catch (err) {
        setErro(err.message || 'Erro ao iniciar pagamento.');
        setCarregando(false);
      }
    } else {
      // pix-manual: mostra QR code após loading
      setTimeout(() => {
        setCarregando(false);
        setMostrarPix(true);
      }, 800);
    }
  };

  // Última tentativa quando a área de transferência não está disponível: seleciona
  // o código na tela e usa o copiar antigo do navegador. Se falhar, o texto fica
  // selecionado para o produtor copiar à mão.
  const copiarSelecionando = () => {
    const el = codigoRef.current;
    if (!el || !window.getSelection) return false;
    try {
      const range = document.createRange();
      range.selectNodeContents(el);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
      return document.execCommand('copy');
    } catch {
      return false;
    }
  };

  // navigator.clipboard não existe fora de contexto seguro (HTTP, WebView antiga) e
  // pode rejeitar; sem tratar, o "Copiado" nunca aparecia e o usuário ficava no vácuo.
  const copiar = async () => {
    setErroCopia('');
    let ok = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(pixPayload);
        ok = true;
      }
    } catch {
      ok = false;
    }
    if (!ok) ok = copiarSelecionando();

    if (ok) {
      setCopiado(true);
      setTimeout(() => setCopiado(false), 3000);
    } else {
      setCopiado(false);
      setErroCopia('Não conseguimos copiar automaticamente neste aparelho. O código acima está selecionável: copie à mão e cole no app do banco, ou pague direto pelo QR Code.');
    }
  };

  return (
    <div className="modal-assinatura">
      <Modal
        titulo={`Assinatura AgriGest — Plano ${plano || 'Básico'}`}
        onClose={onClose}
        bloqueado={bloqueado}
        largura="420px"
      >
        <div className="texto-modal">
          {status === 'TRIAL' && (
            <>
              <p>
                Você está no <strong>período gratuito</strong>
                {dataVenceEm ? <> até <strong>{dataVenceEm}</strong></> : null}
                {diasRestantes != null ? ` (${diasRestantes} dias restantes)` : ''}.
              </p>
              <p>Após o período gratuito, o valor mensal é de <strong>R$ {fmtValor(valorMensal)}</strong>.</p>
            </>
          )}

          {status === 'ATIVO' && (
            <>
              <p>Sua assinatura está <strong className="valor-positivo">ativa</strong>.</p>
              {dataVenceEm && (
                <p>
                  Próxima mensalidade até <strong>{dataVenceEm}</strong>
                  {diasRestantes != null ? ` (${diasRestantes} dias)` : ''}.
                </p>
              )}
              <p>Valor mensal: <strong>R$ {fmtValor(valorMensal)}</strong></p>
            </>
          )}

          {status === 'VENCIDO' && (
            <>
              <p className="valor-negativo" style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                <Icon nome="alerta" tamanho={16} />
                Sua assinatura está vencida{dataVenceuEm ? ` desde ${dataVenceuEm}` : ''}.
              </p>
              {assinatura.mesesDevendo ? (
                <>
                  <p>Meses em atraso: <strong>{assinatura.mesesDevendo}</strong></p>
                  <p>Valor total devido: <strong className="valor-negativo">R$ {fmtValor(valorDevido)}</strong></p>
                </>
              ) : (
                <p>
                  Realize o pagamento para liberar o acesso
                  {dataLiberarAte ? <> até <strong>{dataLiberarAte}</strong></> : null}.
                </p>
              )}

              {outrasFazendas.length > 0 && (
                <div className="paywall-trocar">
                  <div className="paywall-trocar__titulo">
                    Ou continue em outra fazenda:
                  </div>
                  {outrasFazendas.map((f) => (
                    <button
                      key={f.fazendaId}
                      type="button"
                      className="btn-secondary botao-largo paywall-trocar__item"
                      disabled={trocandoFazenda}
                      onClick={() => irParaFazenda(f)}
                    >
                      <Icon nome="empresa" tamanho={14} /> {f.fazendaNome}
                      <span className="paywall-trocar__papel">{f.role === 'ADMIN' ? 'Admin' : 'Membro'}</span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          <div style={{ marginTop: '1.25rem', textAlign: 'center' }}>
            {erro && <div className="msg msg-error">{erro}</div>}
            <button
              type="button"
              onClick={handlePagar}
              disabled={!podePagar || carregando || mostrarPix}
              className={`${podePagar ? 'btn-primary' : 'btn-secondary'} botao-largo`}
            >
              {carregando ? 'Carregando...' : mostrarPix ? 'Dados de pagamento abaixo' : podePagar ? `Pagar R$ ${fmtValor(valorPagar)} via Pix` : 'Pagamento disponível 7 dias antes do vencimento'}
            </button>
            {podePagar && !mostrarPix && (
              <p className="secao-sub">
                {modoPagamento === 'stripe' ? 'Você será redirecionado para a página segura de pagamento.' : 'O QR Code e o código Pix serão exibidos abaixo.'}
              </p>
            )}
          </div>

          {mostrarPix && modoPagamento !== 'stripe' && (
            <div className="pix-caixa">
              <div className="pix-caixa__qr">
                <div className="pix-caixa__qr-fundo">
                  <QRCode value={pixPayload} size={160} />
                </div>
              </div>
              <div className="pix-caixa__hint">Ou copie o código Pix:</div>
              <div ref={codigoRef} onClick={copiar} className="pix-codigo" title="Clique para copiar">
                {pixPayload}
              </div>
              <button type="button" onClick={copiar} className={`pix-copiar ${copiado ? 'pix-copiar--ok' : ''}`}>
                <Icon nome={copiado ? 'ok' : 'copiar'} tamanho={14} />
                {copiado ? 'Copiado!' : 'Clique para copiar'}
              </button>
              {erroCopia && <div className="msg msg-error" role="alert">{erroCopia}</div>}
              <div className="pix-caixa__prazo">
                <Icon nome="relogio" tamanho={14} />
                O pagamento pode levar até 6 horas para ser identificado.
              </div>
            </div>
          )}

          <div className="plano-atual" style={{ marginTop: '1.25rem', textAlign: 'left' }}>
            <div className="login-titulo"><Icon nome="documento" tamanho={16} /> Regras da assinatura</div>
            <ul>
              <li>Valor mensal: <strong>R$ {fmtValor(valorMensal)}</strong></li>
              <li>Pagamento até o dia do vencimento para manter o acesso</li>
              <li>Meses sem pagar acumulam — o valor será a mensalidade × meses em atraso</li>
              <li>Após <strong>6 meses</strong> sem pagamento, a conta será excluída e os dados perdidos</li>
            </ul>
          </div>

          <div className="aviso aviso--atencao">
            <strong>Como identificamos seu pagamento:</strong>
            <ul>
              <li>Na descrição do Pix, escreva o código da sua fazenda: <strong className="codigo-fazenda">{assinatura.codigoConvite || localStorage.getItem('codigoConvite') || '—'}</strong></li>
              <li>Ou envie o comprovante pelo{' '}
                <a href={'https://wa.me/5554999487383?text=' + encodeURIComponent(`Olá! Segue o comprovante de pagamento da fazenda ${assinatura.codigoConvite || localStorage.getItem('codigoConvite') || ''}.`)} target="_blank" rel="noopener noreferrer">
                  Fale Conosco (WhatsApp)
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="modal-rodape modal-rodape--centro">
          {!bloqueado && <button type="button" className="btn-secondary" onClick={onClose}>Fechar</button>}
        </div>
      </Modal>
    </div>
  );
}
